import React from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";

export interface CtaSectionProps {
  onLineClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onPhoneClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const CtaSectionWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1030px;
  height: 427px;
  flex: none;
  flex-grow: 0;
  z-index: 2;
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: auto;
    padding: 40px 16px 40px 16px;
  }
`;

const CtaContentCard = styled.div`
  position: absolute;
  width: 100%;
  max-width: 1030px;
  height: 392px;
  left: 0;
  top: 34.2px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 64px;
  gap: 80px;
  background: url("/static/revise-main-banner.png") no-repeat center, #ffffff;
  background-size: cover;
  border-radius: 16px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    height: auto;
    padding: 32px 32px 0 32px;
    flex-direction: column;
    gap: 24px;
    background: url("/static/revise-main-banner-mobile.png") no-repeat center,
      #ffffff;
    background-size: cover;
  }
`;

const CtaTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  max-width: 902px;
  height: auto;
  flex: none;
  order: 0;
  flex-grow: 0;
  box-sizing: border-box;
`;

const CtaTextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  width: 100%;
  height: auto;
  align-self: stretch;
`;

const CtaTitle = styled.h2`
  width: 100%;
  height: auto;
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  color: #202123;
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 22px;
    line-height: 30px;
  }
`;

const CtaDescription = styled.p`
  width: 100%;
  max-width: 542px;
  height: auto;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #5f6468;
  display: flex;
  align-items: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const CtaButtonsGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  width: 100%;
  max-width: 417px;
  height: auto;

  @media (max-width: 480px) {
    flex-direction: column;
    max-width: 100%;
    width: 100%;
  }
`;

const LineButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 24px 0px 16px;
  width: 170px;
  height: 48px;
  background: #435dff;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  box-sizing: border-box;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PhoneButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  padding: 0px 24px 0px 16px;
  width: 231px;
  height: 48px;
  background: #435dff;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  box-sizing: border-box;

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const ButtonInner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 48px;
  width: 100%;
`;

const LineIconSlot = styled.div`
  width: 24px;
  height: 24px;
  background: url("/static/line-ios.svg") no-repeat center;
  background-size: contain;
`;

const PhoneIconSlot = styled.div`
  width: 24px;
  height: 24px;
  background: url("/static/phone-filled.svg") no-repeat center;
  background-size: contain;
`;

const ButtonText = styled.span`
  height: 24px;
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: #ffffff;
  display: flex;
  align-items: center;
  text-align: center;
  white-space: nowrap;
`;

const CtaImageSlot = styled.div`
  position: absolute;
  width: 405px;
  height: 426px;
  right: 18px;
  top: -34.2px; /* Adjusted to fit relative positioning inside CtaContentCard */
  background: url("/static/icth-presentor.png") no-repeat center;
  background-size: contain;
  pointer-events: none;

  @media (max-width: 1024px) {
    width: 320px;
    height: 337px;
    top: auto;
    bottom: 0px;
  }

  @media (max-width: 768px) {
    display: block;
    position: relative;
    right: auto;
    top: auto;
    bottom: auto;
    width: 320px;
    max-width: 100%;
    height: 337px;
    margin: 16px auto 0 auto;
  }
`;

export const CtaSection = ({ onLineClick, onPhoneClick }: CtaSectionProps) => {
  const { t } = useTranslation("icth-retail-main");

  return (
    <CtaSectionWrapper>
      <CtaContentCard>
        <CtaTextContainer>
          <CtaTextBox>
            <CtaTitle>{t("cta.title")}</CtaTitle>
            <CtaDescription>{t("cta.description")}</CtaDescription>
          </CtaTextBox>
          <CtaButtonsGroup>
            <LineButton onClick={onLineClick}>
              <ButtonInner>
                <LineIconSlot />
                <ButtonText>{t("cta.chatViaLine")}</ButtonText>
              </ButtonInner>
            </LineButton>
            <PhoneButton onClick={onPhoneClick}>
              <ButtonInner>
                <PhoneIconSlot />
                <ButtonText>{t("cta.callCustomerService")}</ButtonText>
              </ButtonInner>
            </PhoneButton>
          </CtaButtonsGroup>
        </CtaTextContainer>
        <CtaImageSlot />
      </CtaContentCard>
    </CtaSectionWrapper>
  );
};

export default CtaSection;
